import classNames from "classnames";
import copy from "copy-to-clipboard";
import moment from "moment";
import React from "react";
import Duration from "~/components/shared/Duration";
import Icon from "~/components/shared/Icon";
import Spinner from "~/components/shared/Spinner";
import { Tooltip } from "~/components/shared/Tooltip";
import { DateLike, formatLogTimestamp } from "~/lib/date";
import parseEmoji from "~/lib/parseEmoji";
import JobLogLineStore from "~/stores/JobLogLineStore";

export type LogSettingsContextType = {
  timestampsOn: boolean;
  timestampsInUTC: boolean;
};

const LogSettingsContext = React.createContext<LogSettingsContextType>({
  timestampsOn: false,
  timestampsInUTC: false,
});

export { LogSettingsContext };

export function Log({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent", className, {
        // Flex column whose body (the log table) is the scroll viewport. Must be
        // able to shrink to the container height (min-h-0, no shrink-0) so the
        // table is bounded and scrolls internally. Drop the base bottom padding
        // so the scrolling table meets the footer flush.
        "flex min-h-0 flex-auto flex-col !rounded-none !pb-0": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent__Controls", className, {
        "z-10 !py-0": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogControl({
  children,
  active,
  ...props
}: (
  | React.ButtonHTMLAttributes<HTMLButtonElement>
  | ({ href: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
) & {
  active?: boolean;
}) {
  const className = classNames(
    "JobLogOutputComponent__Button",
    { "JobLogOutputComponent__Button--Active": active },
    props.className,
  );

  if ("href" in props) {
    return (
      <a {...props} className={className}>
        {children}
      </a>
    );
  }

  return (
    <button {...props} className={className}>
      {children}
    </button>
  );
}

export function LogBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent__Body", className, {
        // Flex column so the log table fills the body and becomes the scroll
        // viewport. `min-h-0` lets it shrink below content height.
        "flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogLoading({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classNames("JobLogOutputComponent__Loader", className)} {...props}>
      {children}
    </div>
  );
}

export function LogMessage({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classNames("p-4", className)} {...props}>
      {children}
    </div>
  );
}

export function LogContent({
  className,
  children,
  totalLines,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  totalLines?: number;
}) {
  const lineNumberWidth = totalLines?.toString().length ?? 0;

  const style = {
    "--line-number-width": `${Math.max(lineNumberWidth, 2)}ch`,
  } as React.CSSProperties;

  return (
    <table
      className={classNames("JobLogOutputComponent__Table", className)}
      style={style}
      {...props}
    >
      {children}
    </table>
  );
}

export function LogGroup({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
}

export const LogGroupHeader = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    expanded?: boolean;
    number?: number;
    highlighted?: boolean;
    deemphasized?: boolean;
  }
>(({ className, number, highlighted, expanded, deemphasized, children, ...props }, ref) => {
  const innerRef = useScrollToHighlighted(highlighted);

  return (
    <tr
      ref={ref ?? innerRef}
      aria-rowindex={number}
      aria-expanded={expanded}
      className={classNames(
        "JobLogOutputComponent__Header group/log-line relative scroll-mt-20 hover:z-[2] has-[[data-state=open]]:z-[2]",
        {
          "JobLogOutputComponent__Header--Deemphasized": deemphasized,
          "JobLogOutputComponent__Header--Highlighted": highlighted,
          "z-[1]": highlighted,
          "has-[[data-state=open]]:![background:var(--theme-bg-3)]": !highlighted,
        },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

export function LogGroupName({
  className,
  name,
  expanded,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  name: string;
  expanded?: boolean;
}) {
  return (
    <td
      className={classNames("JobLogOutputComponent__Header__Name ph-no-capture", className)}
      {...props}
    >
      <button
        className="float-left -ml-1 mr-1 flex h-5 select-none items-center"
        aria-expanded={expanded}
        aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
      >
        <Icon
          icon="heroicons/16/solid/chevron-right"
          className={classNames("h-4 w-4 transition-transform duration-75", {
            "rotate-90": expanded,
          })}
        />
      </button>

      <span
        dangerouslySetInnerHTML={{
          __html: parseEmoji(name, { escape: false }),
        }}
      />
    </td>
  );
}

function useScrollToHighlighted(_highlighted?: boolean) {
  // Scrolling is handled at the page level to prevent race conditions
  return React.useRef<HTMLTableRowElement>(null);
}

export const LogLine = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    number?: number;
    highlighted?: boolean;
  }
>(({ className, number, highlighted, children, ...props }, ref) => {
  const innerRef = useScrollToHighlighted(highlighted);

  return (
    <tr
      ref={ref ?? innerRef}
      aria-rowindex={number}
      className={classNames(
        "JobLogOutputComponent__Line group/log-line relative scroll-mt-20 hover:z-[2] has-[[data-state=open]]:z-[2]",
        {
          "JobLogOutputComponent__Line--Highlighted": highlighted,
          "z-[1]": highlighted,
        },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

export function LogLineContent({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={classNames("JobLogOutputComponent__Line__Content ph-no-capture", className)}
      {...props}
    >
      {children}
    </td>
  );
}

export function LogLineNumber({
  className,
  number,
  actions,
  jobId,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  number: number;
  actions?: React.ReactNode;
  jobId: string;
}) {
  const anchor = JobLogLineStore.getAnchorLine();
  const lineNumberTitle = (() => {
    if (anchor?.jobId === jobId && anchor.lineNumber !== number) {
      const start = Math.min(anchor.lineNumber, number);
      const end = Math.max(anchor.lineNumber, number);
      return (
        <>
          <div>
            Select line <span className="font-medium">{number}</span>
          </div>
          <div>
            (<span className="font-medium italic">Shift+click</span> select{" "}
            <span className="font-medium">
              L{start}
              -L{end}
            </span>
            )
          </div>
        </>
      );
    }
    return (
      <>
        Select line <span className="font-medium">{number}</span>
      </>
    );
  })();

  return (
    <td
      className={classNames(
        "JobLogOutputComponent__Number relative w-[--line-number-width] pl-10 has-[[data-state=open]]:![color:var(--theme-text-1)]",
        className,
      )}
      {...props}
    >
      {actions && (
        <div className="absolute inset-y-0 left-2.5 z-50 flex items-start opacity-0 group-focus-within/log-line:opacity-100 group-hover/log-line:opacity-100 has-[[data-state=open]]:opacity-100">
          {actions}
        </div>
      )}
      <Tooltip tooltip={lineNumberTitle}>
        <span data-line-number={number} className="before:content-[attr(data-line-number)]" />
      </Tooltip>
    </td>
  );
}

export function LogTimestamp({
  className,
  time,
  utc,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  time?: DateLike | null;
  utc?: boolean | null;
}) {
  const timestamp = time ? formatLogTimestamp(time, utc ?? false) : null;

  return (
    <td
      className={classNames(
        "JobLogOutputComponent__Timestamp",
        { "JobLogOutputComponent__Timestamp--UTC": utc },
        className,
      )}
      {...props}
    >
      {timestamp}
    </td>
  );
}

export function LogGroupTime({
  startedAt,
  finishedAt,
  finished,
  durationPercentage,
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  startedAt?: DateLike | null;
  finishedAt?: DateLike | null;
  finished: boolean;
  durationPercentage?: number | null;
}) {
  // Set the width of the badge to it's percentage of time taken to run
  // the group
  let badgeStyle = {};

  if (durationPercentage) {
    badgeStyle = { minWidth: `${durationPercentage}%` };
  } else if (startedAt && !finishedAt) {
    badgeStyle = { width: "20px" };
  }

  // Only show the timing node if we have the full timing information. We
  // could do live countdowns, but because we only get new data every 5
  // seconds, the counter would go up to 6 seconds, we get new data, then it
  // drops to 2 seconds. So it looks funky town.
  return (
    <td className={classNames("JobLogOutputComponent__Time", className)} {...props}>
      {startedAt && (
        <span className="JobLogOutputComponent__Time__Badge text-nowrap" style={badgeStyle}>
          {finishedAt ? (
            <Duration.Short from={startedAt} to={finishedAt} />
          ) : finished ? null : (
            <Spinner color={false} size="8px" className="mx-auto py-0.5" />
          )}
        </span>
      )}
    </td>
  );
}
export function LogCopyPermalink({
  jobId,
  lineNumber,
  label,
}: {
  jobId: string;
  lineNumber: number;
  label: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the line from being unhighlighted
      event.stopPropagation();

      const url = new URL(window.location.href);

      // Check if we have a range highlighted
      const range = JobLogLineStore.getHighlightedRange(jobId);
      if (range && range.start !== range.end) {
        // Range format: #jobId/L{start}-L{end}
        url.hash = `${jobId}/L${range.start}-L${range.end}`;
        copy(url.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        return;
      }

      // Line-only format: #jobId/L{lineNumber}
      url.hash = `${jobId}/L${lineNumber}`;

      copy(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [jobId, lineNumber],
  );

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={handleClick}
      className="btn btn-depth btn-small before:shadow-depth-150 relative flex h-6 w-6 items-center justify-center bg-white p-0"
    >
      {copied ? (
        <Icon icon="heroicons/16/solid/check" className="size-4 text-green-700" />
      ) : (
        <Icon icon="heroicons/16/solid/link" className="size-4" />
      )}
    </button>
  );
}
